import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import type { DynatraceK8sQuery } from 'queries/status-checks/pre-flight/types'
import type { ScenarioGraphNodeType } from 'types/api/scenarios/scenario'
import type { UserDefinedService } from 'types/api/services/service'
import type { EvaluationConfiguration } from 'types/api/status-checks/evaluation'
import type { PagedResponse } from 'types/paged-response'
import type { MultiValueTags } from 'types/tags'
import type { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

enum IntegrationSpecificKeys {
  AWS_ACCESS_KEY_ID = 'AWS_ACCESS_KEY_ID',
  AWS_SECRET_ACCESS_KEY = 'AWS_SECRET_ACCESS_KEY',
  AWS_IAM_ROLE_ARN = 'AWS_IAM_ROLE_ARN'
}

export enum ObservabilityToolType {
  APPDYNAMICS = 'APPDYNAMICS',
  AWS = 'AWS',
  AZURE = 'AZURE',
  CUSTOM = 'CUSTOM',
  DATADOG = 'DATADOG',
  DYNATRACE = 'DYNATRACE',
  GRAFANA = 'GRAFANA',
  NEWRELIC = 'NEWRELIC',
  PAGERDUTY = 'PAGERDUTY',
  PROMETHEUS = 'PROMETHEUS'
}

export enum PredicateType {
  String = 'String',
  Number = 'Number',
  Boolean = 'Boolean'
}

export enum StatusCheckCategory {
  ERRORS = 'ERRORS',
  LATENCY = 'LATENCY',
  REQUESTS = 'REQUESTS',
  UNKNOWN = 'UNKNOWN'
}

export enum TeamExternalIntegrationAuthenticationStatus {
  Authenticated = 'AUTHENTICATED',
  UnAuthenticated = 'UNAUTHENTICATED',
  Untested = 'UNTESTED'
}

/**
 * This is also used on the team integrations page, so the naming convention will be left as is,
 * since its usage there is not Health Check specific.
 */
export interface StatusCheckHeader {
  key: string
  value: string
  label?: string
}

export type EndpointConfiguration = {
  url?: string
} & (
  | { method?: never; payload?: never; headers?: never }
  | { method: 'GET'; payload?: never; headers?: never }
  | {
      method: 'POST'
      payload: string
      headers: { 'content-type': string; [key: string]: string }
    }
)

export interface EndpointResponse {
  httpStatusCode?: number
  requestLatencyMs?: number
  contentType?: string
  responseBody?: string
}

// Shared by both 'StatusCheck' and 'ServiceStatusCheck'
interface StatusCheckBase {
  category: StatusCheckCategory
  categoryAudit?: {
    categorySetBy: string
    categorySetOn: string
  }
  description?: string
  evaluationConfiguration: EvaluationConfiguration
  isContinuous?: boolean
  name: string
}

export interface StatusCheck extends StatusCheckBase {
  createdAt: string
  createdBy: string
  description?: string
  endpointConfiguration: EndpointConfiguration
  identifier: string
  isPrivateNetwork?: boolean
  multiSelectTags?: MultiValueTags
  pollingIntervalSeconds?: number
  rawEndpointConfiguration?: EndpointConfiguration
  requestParams?: Record<string, string>
  teamExternalIntegration: StatusCheckTeamExternalIntegration
  teamId: string
  thirdPartyPresets?: string
  type?: ScenarioGraphNodeType
}

export interface StatusCheckTeamExternalIntegration {
  name: string
  type?: IntegrationType
  domain?: string | null
  observabilityToolType?: ObservabilityToolType
  integrationSpecificValues?: IntegrationSpecificValues
}

// can omit sending if it's null
export type EvaluationConfigurationCreateRequest = Omit<
  EvaluationConfiguration,
  'okLatencyMaxMs'
> & {
  okLatencyMaxMs?: number
}

interface SharedStatusCheckCreateRequest
  extends Pick<
    StatusCheck,
    | 'isContinuous'
    | 'isPrivateNetwork'
    | 'multiSelectTags'
    | 'name'
    | 'pollingIntervalSeconds'
    | 'thirdPartyPresets'
  > {
  teamExternalIntegration?: StatusCheckTeamExternalIntegration
  webhookId?: string
}
export interface StatusCheckCreateRequest
  extends SharedStatusCheckCreateRequest {
  category?: StatusCheckCategory
  description?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfigurationCreateRequest
  rawEndpointConfiguration: Omit<EndpointConfiguration, 'method'>
}

export type DynatraceStatusCheckCreateRequest =
  SharedStatusCheckCreateRequest & {
    category: StatusCheckCategory
  } & ({ entityId?: string } | { k8sQuery?: DynatraceK8sQuery })

export type DynatraceStatusCheckCreateResponse = Omit<
  UserDefinedService,
  'serviceId'
> & { identifier: string }

export interface StatusCheckEvaluationPredicate {
  type?: PredicateType
  jqQuery?: string
  jpQuery?: string
  comparator?: string
  rValue?: any
}

export interface ResponseBodyEvaluation {
  op: 'AND' | 'OR'
  predicates?: StatusCheckEvaluationPredicate[]
}

export interface ServiceStatusCheck extends StatusCheckBase {
  autoGeneratedFromIdentifier?: string
  id: string
  integration: TeamExternalIntegration
  isAutogenerated?: boolean
}

/*
 * When we transform a `StatusCheck` to a `ServiceStatusCheck`, we now need to include additional
 * fields from the StatusCheck. These fields are needed to populate fields on the Health Check
 * Create/Edit forms. We don't wish to add these to `ServiceStatusCheck`, as this doesn't align
 * with the data model of the response from `services/:serviceId/status-checks`.
 */
export type ServiceStatusCheckFromStatusCheck = Omit<
  ServiceStatusCheck,
  'evaluationConfiguration'
> &
  Pick<StatusCheck, 'multiSelectTags' | 'requestParams'> &
  Pick<
    StatusCheckFormValues,
    | 'evaluationConfiguration'
    | 'endpointConfiguration'
    | 'pollingIntervalSeconds'
  >

export type IntegrationSpecificValues = {
  [key in IntegrationSpecificKeys]?: string
}

export interface TeamExternalIntegration {
  authUrl?: string
  canBeUsedAsStatusCheck?: boolean
  createdAt?: string
  domain?: string | null
  headers?: StatusCheckHeader[]
  headersArn: string | null
  id?: string
  integrationSpecificValues?: IntegrationSpecificValues
  isConfigured?: boolean
  isOther?: boolean
  isPrivateNetworkIntegration?: boolean
  lastAuthenticationStatus?: TeamExternalIntegrationAuthenticationStatus
  name: string
  observabilityToolType?: ObservabilityToolType
  sortIndex?: number
  type: IntegrationType
  url?: string
}

export interface TeamExternalIntegrationResponse {
  canBeUsedAsStatusCheck: boolean
  createdAt: string
  domain: string | null
  headers?: Record<string, string>
  headersArn: string | null
  integrationSpecificValues?: IntegrationSpecificValues
  lastAuthenticationStatus: TeamExternalIntegrationAuthenticationStatus
  name: string
  observabilityToolType: ObservabilityToolType
  privateNetwork: boolean
  teamId: string
  type: IntegrationType // TODO https://gremlininc.atlassian.net/browse/EN-9101
  url: string
  userId: string
}

export type PagedStatusChecksResponse = PagedResponse<StatusCheck>
